import type { RequestInit } from '@vercel/fetch'
import {
    GraphQLError,
    GraphqlFetchOutput,
    graphqlFetch
} from '@pylot-data/graphqlFetch'
import type { LegacySwrConfiguration } from '@pylot-data/hooks/utils/normalizeInitialData'
import { normalizeInitialData } from '@pylot-data/hooks/utils/normalizeInitialData'
import useSWRInfinite from 'swr/infinite'
import type {
    SWRInfiniteConfiguration,
    SWRInfiniteResponse
} from 'swr/infinite'
import { useRouter } from 'next/router'

/** Each page in the infinite list is the full GraphQL response (data + errors). */
export type SwrUsePaginatedQueryOutput<OutputInterface> = SWRInfiniteResponse<
    GraphqlFetchOutput<OutputInterface>,
    GraphQLError
>

export type UsePaginatedQuerySwrOptions =
    LegacySwrConfiguration<SWRInfiniteConfiguration> & {
        /**
         * Páginas a cargar al montar cuando se vuelve a un listado (restauración
         * de scroll). Reemplaza a `initialSize` solo si es mayor; los hooks que
         * calculan el offset de páginas visibles siguen usando `initialSize`.
         */
        restoreSize?: number
    }

export const usePaginatedQuery = <
    VariablesInterface,
    OutputInterface,
    OutputError = GraphQLError
>(
    query: string | undefined | null,
    variablesRaw?: VariablesInterface | null,
    sizeVariableName?: keyof VariablesInterface | null,
    swrOptions?: UsePaginatedQuerySwrOptions,
    fetchOptions?: RequestInit,
    subsequentRequestsPageSize?: number,
    customPylotQueryHeader?: string
): SWRInfiniteResponse<GraphqlFetchOutput<OutputInterface>, OutputError> => {
    const { locale } = useRouter()

    if (variablesRaw) {
        if (!sizeVariableName) {
            throw new Error(
                'UsePaginatedQuery: Variable name for size value is not indicated'
            )
        }
        if (!(sizeVariableName in (variablesRaw as Record<string, unknown>))) {
            throw new Error(
                `UsePaginatedQuery: Property '${String(
                    sizeVariableName
                )}' is not defined inside the object with variables`
            )
        }
    }

    function getVars(
        pageIndex: number,
        previousPageData: GraphqlFetchOutput<OutputInterface> | null
    ) {
        if (!query) return null

        // Don't request a page beyond the last one (avoids API error "currentPage > total_pages")
        if (
            pageIndex > 0 &&
            previousPageData &&
            typeof previousPageData === 'object'
        ) {
            const payload = previousPageData.data as
                | {
                      products?: {
                          page_info?: {
                              current_page?: number
                              total_pages?: number
                          }
                      }
                      productSearch?: {
                          page_info?: {
                              current_page?: number
                              total_pages?: number
                          }
                      }
                      customer?: {
                          orders?: {
                              page_info?: {
                                  current_page?: number
                                  total_pages?: number
                              }
                          }
                      }
                  }
                | undefined
            const pageInfo =
                payload?.products?.page_info ??
                payload?.productSearch?.page_info ??
                payload?.customer?.orders?.page_info
            const total_pages = pageInfo?.total_pages
            const current_page = pageInfo?.current_page
            if (
                total_pages != null &&
                current_page != null &&
                current_page >= total_pages
            ) {
                return null
            }
            // Una página que volvió con errores y sin page_info no dice si hay más: pedir la
            // siguiente solo repite el error (p. ej. currentPage > total_pages cuando el
            // catálogo se achicó con la pestaña abierta) y encadena peticiones. El reintento
            // revalida esa página y, si responde, la paginación sigue.
            if (!pageInfo && previousPageData.errors?.length) {
                return null
            }
        }

        if (!variablesRaw) {
            return [query]
        }

        const variables: VariablesInterface = {
            ...variablesRaw!
        }

        if (sizeVariableName) {
            ;(variables[sizeVariableName] as unknown as number) = pageIndex + 1
        }

        //@ts-ignore
        if (subsequentRequestsPageSize && variables[sizeVariableName] > 1) {
            //@ts-ignore
            variables.pageSize = subsequentRequestsPageSize
            //@ts-ignore
            variables[sizeVariableName] = variables[sizeVariableName] + 1
        }

        const sortedVariables = Object.entries(
            variables as Record<string, unknown>
        ).sort(([key1], [key2]) => {
            if (key1 < key2) return -1
            else if (key1 > key2) return 1
            else return 0
        })

        const queryKey = [
            query,
            ...sortedVariables!.map((variableEntry) =>
                JSON.stringify(variableEntry[1])
            )
        ]

        return queryKey ?? null
    }

    const { restoreSize, ...swrOptionsRest } = swrOptions ?? {}
    const configuredInitialSize = swrOptionsRest.initialSize ?? 1
    const effectiveSwrOptions =
        restoreSize && restoreSize > configuredInitialSize
            ? { ...swrOptionsRest, initialSize: restoreSize }
            : swrOptionsRest

    return useSWRInfinite(
        getVars,
        // SWR v2 no spreadea los elementos de una key-array como argumentos
        // separados: el fetcher recibe la key completa como único argumento.
        ([query, ...variableValues]: [string, ...string[]]) => {
            const sortedVariables = variablesRaw
                ? Object.entries(variablesRaw as Record<string, unknown>).sort(
                      ([key1], [key2]) => {
                          if (key1 < key2) return -1
                          if (key1 > key2) return 1
                          return 0
                      }
                  )
                : null
            const variables = variableValues.length
                ? (variableValues.reduce(
                      (obj: Record<string, unknown>, val, i) => {
                          obj[sortedVariables![i][0]!] = JSON.parse(val)
                          return obj
                      },
                      {} as Record<string, unknown>
                  ) as VariablesInterface)
                : undefined
            const res = graphqlFetch<VariablesInterface, OutputInterface>({
                query: query!,
                variables,
                fetchOptions,
                locale,
                customPylotQueryHeader
            })
            return res
        },
        normalizeInitialData(effectiveSwrOptions)
    )
}
